import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const ChapterGridTooltip = ({
    children,
    chapterStatuses = [],
    language,
    isQuiz = false,
    hasActiveSession = false,
    mode = 'global', // 'global' | 'session' | 'score' | 'comprehension'
    scoreAnswers = null,       // For score mode: {chapterId: answerId}
    correctAnswers = null,     // For score mode: {chapterId: correctReponseId}
    scoreDescription = '',     // For score mode: custom description text
    customContent = null,      // When provided, renders this JSX instead of grid content
    className = ''
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, showBelow: false });
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const leaveTimer = useRef(null);

    const visible = isOpen || isHovering;

    // Calculate position from trigger element with smart placement
    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipHeight = tooltipRef.current?.offsetHeight || 220;
        const tooltipWidth = 220;

        // Check if there's enough room above
        const showBelow = rect.top < tooltipHeight + 20;

        // Clamp horizontal position
        let left = rect.left;
        if (left + tooltipWidth > window.innerWidth - 10) {
            left = Math.max(10, window.innerWidth - tooltipWidth - 10);
        }

        setPosition({
            top: showBelow ? rect.bottom + 12 : rect.top - 12,
            left,
            showBelow,
        });
    }, []);

    useEffect(() => {
        if (visible) {
            updatePosition();
            // Re-measure after tooltip renders to get actual height
            const frame = requestAnimationFrame(updatePosition);
            return () => cancelAnimationFrame(frame);
        }
    }, [visible, updatePosition]);

    // Close on scroll
    useEffect(() => {
        if (!visible) return;
        const onScroll = () => { setIsOpen(false); setIsHovering(false); };
        window.addEventListener('scroll', onScroll, { capture: true, passive: true });
        return () => window.removeEventListener('scroll', onScroll, { capture: true });
    }, [visible]);

    // Close on click outside (mobile)
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (triggerRef.current?.contains(e.target)) return;
            if (tooltipRef.current?.contains(e.target)) return;
            setIsOpen(false);
        };
        document.addEventListener('pointerdown', handler);
        return () => document.removeEventListener('pointerdown', handler);
    }, [isOpen]);

    // Cleanup timer
    useEffect(() => {
        return () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); };
    }, []);

    const handleMouseEnter = () => {
        if (window.matchMedia('(min-width: 768px)').matches) {
            if (leaveTimer.current) clearTimeout(leaveTimer.current);
            setIsHovering(true);
        }
    };
    const handleMouseLeave = () => {
        leaveTimer.current = setTimeout(() => setIsHovering(false), 150);
    };

    const total = chapterStatuses.length;
    const isSessionMode = mode === 'session';
    const isScoreMode = mode === 'score';
    const isComprehensionMode = mode === 'comprehension';

    // Score mode counts
    let scoreCorrectCount = 0;
    let scoreIncorrectCount = 0;
    if (isScoreMode && scoreAnswers && correctAnswers) {
        chapterStatuses.forEach(ch => {
            const userAnswer = scoreAnswers[ch.chId];
            if (userAnswer !== undefined && userAnswer !== null) {
                if (String(userAnswer) === String(correctAnswers[ch.chId])) {
                    scoreCorrectCount++;
                } else {
                    scoreIncorrectCount++;
                }
            }
        });
    }

    const activeCount = isSessionMode
        ? chapterStatuses.filter(ch => ch.sv).length
        : isComprehensionMode
            ? chapterStatuses.filter(ch => ch.c).length
            : chapterStatuses.filter(ch => ch.v).length;
    const itemLabel = isQuiz
        ? (language === 'fr' ? 'questions' : 'questions')
        : (language === 'fr' ? 'sections' : 'sections');

    if (total === 0 && !customContent) return <>{children}</>;
    // In score mode, skip tooltip if no answer data
    if (isScoreMode && !scoreAnswers && !customContent) return <>{children}</>;

    // Description text based on mode
    let description;
    if (isScoreMode) {
        description = scoreDescription || (language === 'fr' ? 'Résultat par question.' : 'Per-question result.');
    } else if (isComprehensionMode) {
        description = language === 'fr'
            ? 'Questions répondues correctement au moins une fois.'
            : 'Questions answered correctly at least once.';
    } else if (isSessionMode) {
        description = isQuiz
            ? (language === 'fr'
                ? 'Questions répondues dans la session en cours.'
                : 'Questions answered in the current session.')
            : (language === 'fr'
                ? 'Sections validées dans la session en cours.'
                : 'Sections validated in the current session.');
    } else {
        if (isQuiz && hasActiveSession) {
            description = language === 'fr'
                ? 'Avancement de la session en cours.'
                : 'Current session progress.';
        } else {
            description = isQuiz
                ? (language === 'fr'
                    ? 'Questions répondues au moins une fois dans ce quiz.'
                    : 'Questions answered at least once in this quiz.')
                : (language === 'fr'
                    ? 'Sections validées au moins une fois dans ce chapitre.'
                    : 'Sections validated at least once in this chapter.');
        }
    }

    const tooltipPanel = visible ? createPortal(
        <div
            ref={tooltipRef}
            onMouseEnter={() => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }}
            onMouseLeave={() => { leaveTimer.current = setTimeout(() => setIsHovering(false), 150); }}
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: position.showBelow ? 'none' : 'translateY(-100%)',
                zIndex: 9999,
                width: '220px',
            }}
            className={`transition-[opacity,transform] duration-200 ${position.showBelow ? 'origin-top-left' : 'origin-bottom-left'} ${
                visible ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
            }`}
        >
            {/* Arrow top (when tooltip is below trigger) */}
            {position.showBelow && (
                <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-l border-t border-white/10" />
            )}
            <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-3 shadow-2xl border border-white/10">
                {customContent ? customContent : (<>
                {/* Description */}
                <p className="text-[10px] text-gray-300 mb-2 leading-relaxed">
                    {description}
                </p>

                {/* Counter */}
                <p className="text-[11px] font-black text-white mb-2">
                    {isScoreMode
                        ? `${scoreCorrectCount} ${language === 'fr' ? 'juste' : 'correct'} / ${scoreIncorrectCount} ${language === 'fr' ? 'faux' : 'wrong'} / ${total - scoreCorrectCount - scoreIncorrectCount} ${language === 'fr' ? 'non rép.' : 'skipped'}`
                        : `${activeCount} / ${total} ${itemLabel}`
                    }
                </p>

                {/* Grid */}
                {total > 0 && (
                    <div className="grid grid-cols-10 gap-[3px]">
                        {chapterStatuses.map((ch, idx) => {
                            let cellColor = 'bg-gray-600';
                            if (isScoreMode) {
                                const userAnswer = scoreAnswers?.[ch.chId];
                                if (userAnswer !== undefined && userAnswer !== null) {
                                    if (String(userAnswer) === String(correctAnswers?.[ch.chId])) {
                                        cellColor = 'bg-emerald-500';
                                    } else {
                                        cellColor = 'bg-red-500';
                                    }
                                }
                            } else if (isComprehensionMode) {
                                if (ch.c) cellColor = 'bg-purple-500';
                            } else if (isSessionMode) {
                                if (ch.sv) cellColor = 'bg-blue-500';
                            } else {
                                if (isQuiz) {
                                    // Quiz: blue for session, sky for historic, gray for unanswered
                                    if (hasActiveSession && ch.sv) cellColor = 'bg-blue-500';
                                    else if (ch.v) cellColor = hasActiveSession ? 'bg-sky-300' : 'bg-blue-500';
                                } else {
                                    // Course: green for validated; blue for session only when hasActiveSession=true
                                    if (hasActiveSession && ch.sv) cellColor = 'bg-blue-500';
                                    else if (ch.v) cellColor = 'bg-green-500';
                                }
                            }

                            return (
                                <div
                                    key={idx}
                                    className={`w-[14px] h-[14px] rounded-sm ${cellColor}`}
                                />
                            );
                        })}
                    </div>
                )}

                {/* Legend */}
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-white/10">
                    {isScoreMode ? (
                        <>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-emerald-500"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Juste' : 'Correct'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-red-500"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Faux' : 'Wrong'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Non rép.' : 'Skip'}</span>
                            </div>
                        </>
                    ) : isComprehensionMode ? (
                        <>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-purple-500"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Compris' : 'Correct'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                <span className="text-[8px] text-gray-400">{language === 'fr' ? 'À revoir' : 'To review'}</span>
                            </div>
                        </>
                    ) : isSessionMode ? (
                        <>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-blue-500"></div>
                                <span className="text-[8px] text-gray-400">Session</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                <span className="text-[8px] text-gray-400">
                                    {language === 'fr' ? 'À faire' : 'To do'}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            {isQuiz && hasActiveSession ? (
                                <>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-sm bg-blue-500"></div>
                                        <span className="text-[8px] text-gray-400">Session</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-sm bg-sky-300"></div>
                                        <span className="text-[8px] text-gray-400">{language === 'fr' ? 'Déjà vu' : 'Seen'}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                        <span className="text-[8px] text-gray-400">{language === 'fr' ? 'À faire' : 'To do'}</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center gap-1">
                                        <div className={`w-2 h-2 rounded-sm ${isQuiz ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                                        <span className="text-[8px] text-gray-400">
                                            {language === 'fr' ? 'Validé' : 'Done'}
                                        </span>
                                    </div>
                                    {!isQuiz && hasActiveSession && (
                                        <div className="flex items-center gap-1">
                                            <div className="w-2 h-2 rounded-sm bg-blue-500"></div>
                                            <span className="text-[8px] text-gray-400">Session</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-sm bg-gray-600"></div>
                                        <span className="text-[8px] text-gray-400">
                                            {language === 'fr' ? 'À faire' : 'To do'}
                                        </span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
                </>)}
            </div>
            {/* Arrow bottom (when tooltip is above trigger) */}
            {!position.showBelow && (
                <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-r border-b border-white/10" />
            )}
        </div>,
        document.body
    ) : null;

    return (
        <>
            <div
                ref={triggerRef}
                onClick={() => setIsOpen(v => !v)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={`cursor-help select-none ${className}`}
            >
                {children}
            </div>
            {tooltipPanel}
        </>
    );
};

export default ChapterGridTooltip;
